import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import "./EstimatorCalculator.css";
import { FiDownload } from "react-icons/fi";
import { calculateEstimatorResult, formatValue } from "./calculatorLogic";
import { downloadEstimatorPdf } from "./pdfGenerator";
import RequestCallModal from "../../../shared/RequestCallModal/RequestCallModal";

const EstimatorCalculator = () => {
  const { t, i18n } = useTranslation();
  const [isRequestCallOpen, setIsRequestCallOpen] = useState(false);
  const [form, setForm] = useState({
    height: "",
    width: "",
    frameType: "single",
    boardSides: "one",
  });
  const [result, setResult] = useState({
    area: 0,
    unit: "m²",
    Board: 0,
    Screws: 0,
    Screws25: 0,
    Screws35: 0,
    CW: 0,
    UD: 0,
    Filler: 0,
    WaferHead: 0,
    Anchors: 0,
    Tape: 0,
    calculated: false,
  });

  const handleChange = (field) => (event) => {
    const { value } = event.target;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCalculate = () => {
    setResult(calculateEstimatorResult(form));
  };

  // PDF is always English regardless of UI language
  const getPdfLabels = () => {
    const tEn = i18n.getFixedT("en");
    const frameLabel =
      form.frameType === "double"
        ? tEn("sections.estimator.calculator.frameDouble")
        : tEn("sections.estimator.calculator.frameSingle");
    const boardSidesLabel =
      form.boardSides === "two"
        ? tEn("sections.estimator.calculator.boardTwo")
        : tEn("sections.estimator.calculator.boardOne");

    return {
      description: tEn("sections.estimator.calculator.description"),
      amount: tEn("sections.estimator.calculator.amount"),
      totalArea: tEn("sections.estimator.calculator.totalArea", { unit: result.unit }),
      board: tEn("sections.estimator.calculator.board"),
      trackUd: tEn("sections.estimator.calculator.trackUd"),
      studCw: tEn("sections.estimator.calculator.studCw"),
      jointTape: tEn("sections.estimator.calculator.jointTape"),
      jointCompound: tEn("sections.estimator.calculator.jointCompound"),
      screws25: tEn("sections.estimator.calculator.screws25"),
      screws35: tEn("sections.estimator.calculator.screws35"),
      waferHead: tEn("sections.estimator.calculator.waferHead"),
      anchors: tEn("sections.estimator.calculator.anchors"),
      studSpacing: tEn("sections.estimator.calculator.studSpacing"),
      studSpacingValue: tEn("sections.estimator.calculator.studSpacingValue"),
      waste: tEn("sections.estimator.calculator.waste"),
      wasteValue: tEn("sections.estimator.calculator.wasteValue"),
      unitPcs: tEn("sections.estimator.calculator.unitPcs"),
      unitM: tEn("sections.estimator.calculator.unitM"),
      unitKg: tEn("sections.estimator.calculator.unitKg"),
      pdfTitle: tEn("sections.estimator.calculator.pdfTitle"),
      pdfInput: tEn("sections.estimator.calculator.pdfInput", {
        height: formatValue(form.height || 0),
        width: formatValue(form.width || 0),
        frame: frameLabel,
        boardSides: boardSidesLabel,
      }),
      pdfDateGenerated: tEn("sections.estimator.calculator.pdfDateGenerated", {
        date: new Date().toLocaleDateString("en-US"),
      }),
      pdfNoteLabel: tEn("sections.estimator.calculator.pdfNoteLabel"),
      pdfNoteText: tEn("sections.estimator.calculator.pdfNoteText"),
    };
  };

  const handleDownloadPdf = async () => {
    await downloadEstimatorPdf({ form, result, labels: getPdfLabels() });
  };

  return (
    <section className="estimator-calc">

      {/* TOP HEADER */}
      <div className="estimator-header">
        <span className="badge">{t("sections.estimator.calculator.badge")}</span>

        <h1>
          {t("sections.estimator.calculator.title")}{" "}
          <span>{t("sections.estimator.calculator.titleAccent")}</span>
        </h1>

        <p>{t("sections.estimator.calculator.subtitle")}</p>
      </div>

      {/* MAIN CONTENT */}
      <div className="estimator-grid">

        {/* LEFT PANEL */}
        <div className="calc-box">
          <h3>{t("sections.estimator.calculator.calculateArea")}</h3>

          <input
            type="number"
            min="0"
            placeholder={t("sections.estimator.calculator.heightPlaceholder")}
            value={form.height}
            onChange={handleChange("height")}
          />

          <input
            type="number"
            min="0"
            placeholder={t("sections.estimator.calculator.widthPlaceholder")}
            value={form.width}
            onChange={handleChange("width")}
          />

          <select value={form.frameType} onChange={handleChange("frameType")}>
            <option value="single">{t("sections.estimator.calculator.frameSingle")}</option>
            <option value="double">{t("sections.estimator.calculator.frameDouble")}</option>
          </select>

          <select value={form.boardSides} onChange={handleChange("boardSides")}>
            <option value="one">{t("sections.estimator.calculator.boardOne")}</option>
            <option value="two">{t("sections.estimator.calculator.boardTwo")}</option>
          </select>

          <button className="calculate-btn" onClick={handleCalculate} type="button">
            {t("sections.estimator.calculator.calculate")}
          </button>

          <p className="note">{t("sections.estimator.calculator.note")}</p>

          <p className="callback">
            {t("sections.estimator.calculator.callbackPrefix")}{" "}
            <button
              type="button"
              className="callback-link"
              onClick={() => setIsRequestCallOpen(true)}
            >
              {t("sections.estimator.calculator.callbackLink")}
            </button>
          </p>
        </div>

        {/* RIGHT PANEL */}
        <div className="result-box">
          <div className="result-header">
            <span>{t("sections.estimator.calculator.description")}</span>
            <span>{t("sections.estimator.calculator.amount")}</span>
          </div>

          <div className="result-row">
            <span>{t("sections.estimator.calculator.totalArea", { unit: result.unit })}</span>
            <span>
              {formatValue(result.area, 0)} {result.unit}
            </span>
          </div>

          <div className="result-row">
            <span>{t("sections.estimator.calculator.board")}</span>
            <span>
              {result.Board} {t("sections.estimator.calculator.unitPcs")}
            </span>
          </div>

          <div className="result-row">
            <span>{t("sections.estimator.calculator.trackUd")}</span>
            <span>
              {formatValue(result.UD, 0)} {t("sections.estimator.calculator.unitM")}
            </span>
          </div>

          <div className="result-row">
            <span>{t("sections.estimator.calculator.studCw")}</span>
            <span>
              {formatValue(result.CW, 0)} {t("sections.estimator.calculator.unitM")}
            </span>
          </div>

          <div className="result-row">
            <span>{t("sections.estimator.calculator.jointTape")}</span>
            <span>
              {formatValue(result.Tape, 0)} {t("sections.estimator.calculator.unitM")}
            </span>
          </div>

          <div className="result-row">
            <span>{t("sections.estimator.calculator.jointCompound")}</span>
            <span>
              {formatValue(result.Filler, 0)} {t("sections.estimator.calculator.unitKg")}
            </span>
          </div>

          <div className="result-row">
            <span>{t("sections.estimator.calculator.screws25")}</span>
            <span>
              {formatValue(result.Screws25, 0)} {t("sections.estimator.calculator.unitPcs")}
            </span>
          </div>

          {form.boardSides === "two" && (
            <div className="result-row">
              <span>{t("sections.estimator.calculator.screws35")}</span>
              <span>
                {formatValue(result.Screws35, 0)} {t("sections.estimator.calculator.unitPcs")}
              </span>
            </div>
          )}

          <div className="result-row">
            <span>{t("sections.estimator.calculator.waferHead")}</span>
            <span>
              {formatValue(result.WaferHead, 0)} {t("sections.estimator.calculator.unitPcs")}
            </span>
          </div>

          <div className="result-row">
            <span>{t("sections.estimator.calculator.anchors")}</span>
            <span>
              {formatValue(result.Anchors, 0)} {t("sections.estimator.calculator.unitPcs")}
            </span>
          </div>

          <div className="result-row">
            <span>{t("sections.estimator.calculator.studSpacing")}</span>
            <span>{t("sections.estimator.calculator.studSpacingValue")}</span>
          </div>

          <div className="result-row">
            <span>{t("sections.estimator.calculator.waste")}</span>
            <span>{t("sections.estimator.calculator.wasteValue")}</span>
          </div>

          {!result.calculated && (
            <p className="result-hint">{t("sections.estimator.calculator.resultHint")}</p>
          )}
        </div>

      </div>

      {/* DOWNLOAD BUTTON */}
      <div className="download-wrapper">
        <button
          className="download-btn buttongyp"
          type="button"
          onClick={handleDownloadPdf}
          disabled={!result.calculated}
        >
          {t("sections.estimator.calculator.downloadPdf")}
          <FiDownload />
        </button>
      </div>

      <RequestCallModal
        isOpen={isRequestCallOpen}
        onClose={() => setIsRequestCallOpen(false)}
      />
    </section>
  );
};

export default EstimatorCalculator;
